import { X, Minus, Plus, ClipboardList, Share2, Loader2 } from "lucide-react";
import { money, unitPrice, variantText } from "@/lib/api";

export const SummaryPanel = ({ lines, total, shippingTotal = 0, onRemove, onQty, catLabels, optionSets, onShare, sharing }) => {
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b border-border pb-3">
        <ClipboardList size={18} className="text-[#1B66C9]" />
        <h2 className="font-display text-sm font-black uppercase tracking-[0.2em]">Your Build</h2>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto py-4" data-testid="summary-items">
        {lines.length === 0 && (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No items yet. Pick a hull to get started.
          </p>
        )}
        {lines.map((l) => {
          const unit = unitPrice(l.product, l.variants, l.addons, optionSets);
          const vt = variantText(l.variants, l.addons);
          return (
            <div key={l.product.id} className="flex gap-3" data-testid={`summary-item-${l.product.id}`}>
              {l.product.image_url ? (
                <img
                  src={l.product.image_url}
                  alt=""
                  className="h-14 w-14 flex-shrink-0 rounded-md object-cover"
                />
              ) : (
                <span className="h-14 w-14 flex-shrink-0 rounded-md bg-secondary" />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold leading-tight">{l.product.name}</p>
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
                      {l.product.brand ? `${l.product.brand} · ` : ""}{catLabels[l.product.category] || l.product.category}
                    </p>
                    {vt && (
                      <p data-testid={`summary-variants-${l.product.id}`} className="line-clamp-2 text-[11px] text-muted-foreground">
                        {vt}
                      </p>
                    )}
                  </div>
                  <button
                    data-testid={`summary-remove-${l.product.id}`}
                    onClick={() => onRemove(l.product)}
                    className="text-muted-foreground transition-colors hover:text-[#DC2626]"
                  >
                    <X size={15} />
                  </button>
                </div>
                <div className="mt-1 flex items-center justify-between">
                  {l.selectType === "multi" ? (
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => onQty(l.product, Math.max(1, l.quantity - 1))}
                        className="flex h-5 w-5 items-center justify-center rounded border border-border hover:bg-secondary"
                      >
                        <Minus size={11} />
                      </button>
                      <span className="min-w-5 text-center text-xs font-bold">{l.quantity}{l.product.unit ? ` ${l.product.unit}` : ""}</span>
                      <button
                        onClick={() => onQty(l.product, l.quantity + 1)}
                        className="flex h-5 w-5 items-center justify-center rounded border border-border hover:bg-secondary"
                      >
                        <Plus size={11} />
                      </button>
                    </div>
                  ) : (
                    <span className="text-[11px] text-muted-foreground">Qty 1</span>
                  )}
                  <span className="text-sm font-bold">{money(unit * l.quantity)}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="border-t border-border pt-4">
        {shippingTotal > 0 && (
          <div className="mb-2 flex items-center justify-between text-sm" data-testid="summary-shipping">
            <span className="text-muted-foreground">Shipping <span className="text-[11px]">(flat rate, included)</span></span>
            <span className="font-bold">{money(shippingTotal)}</span>
          </div>
        )}
        <div className="flex items-end justify-between">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
            Est. Total
          </span>
          <span
            key={total}
            data-testid="summary-total"
            className="font-display text-3xl font-black text-[#1B66C9] animate-in fade-in slide-in-from-bottom-1 duration-300"
          >
            {money(total)}
          </span>
        </div>
        <p className="mt-2 text-[11px] leading-snug text-muted-foreground">
          Estimate only. Pricing subject to change at time of build.
        </p>
        {onShare && lines.length > 0 && (
          <button
            data-testid="share-build-button"
            onClick={onShare}
            disabled={sharing}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-md border border-border py-2.5 text-xs font-bold uppercase tracking-wider transition-colors hover:bg-secondary disabled:opacity-60"
          >
            {sharing ? <Loader2 size={14} className="animate-spin" /> : <Share2 size={14} />}
            Save & Share Build
          </button>
        )}
      </div>
    </div>
  );
};
